/**
 * Approvals in Tasks (0.31.0): what you can decide, what you asked for (or shared for approval), and every
 * approval you may see, each with its thread of notes. Also the pieces the asset page's Sharing tab reuses.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, CircleCheck, CircleDashed, CircleX, Clock, Film, Hand, Link2, ListChecks, User, Users, Workflow, X } from 'lucide-react';
import { framesToTimecode, formatTimecode, parseTimecode, secondsToTicks, ticksToFrames, type ApprovalDTO, type ApprovalStatus, type Rational } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useApproval, useApprovalNotes, useApprovals, useAsset } from '../api/hooks';
import { toast } from '../components/Toasts';
import { clock, dateTime, relative } from '../lib/format';
import { NoteComposer, Thread, type ThreadMessage } from '../components/Notes';
import { EmptyState } from '../components/EmptyState';
import { Segmented } from '../components/Segmented';
import { queryKeys } from '../api/queryKeys';
import { LoadMore } from '../components/LoadMore';

const APPROVAL_STATUS_LABEL: Record<ApprovalStatus, string> = {
  pending: 'Waiting', approved: 'Approved', rejected: 'Rejected', cancelled: 'Cancelled', expired: 'Expired',
};
/** A share link's or a person's "rejected" is a request for changes. */
export const statusLabel = (a: Pick<ApprovalDTO, 'status' | 'source'>) => (a.status === 'rejected' && a.source !== 'flow' ? 'Changes requested' : APPROVAL_STATUS_LABEL[a.status]);

export function ApprovalStatusIcon({ status }: { status: ApprovalStatus }) {
  if (status === 'approved') return <CircleCheck size={14} color="var(--ready)" />;
  if (status === 'rejected') return <CircleX size={14} color="var(--danger)" />;
  if (status === 'pending') return <Hand size={14} color="var(--warning)" />;
  return <CircleDashed size={14} color="var(--faint)" />;
}

export function approverLabel(a: ApprovalDTO): string {
  if (a.approver.type === 'link') return 'Anyone with the link';
  if (a.approver.type === 'user') return a.approver.name;
  return `Any ${a.approver.name}`;
}

const SOURCE_ICON = { flow: <Workflow size={11} />, share: <Link2 size={11} />, asset: <User size={11} /> } as const;

/** Position labels: source timecode when the asset's timing is known, otherwise h:mm:ss. */
interface NoteTiming { rate: Rational; startFrames: number }
export function timingOf(asset: { timing: { sourceFrameRate: Rational | null; startTimecode: string | null }; frameRate: Rational | null } | undefined): NoteTiming | null {
  if (!asset) return null;
  const rate = asset.timing.sourceFrameRate ?? asset.frameRate ?? { num: 25, den: 1 };
  return { rate, startFrames: asset.timing.startTimecode ? parseTimecode(asset.timing.startTimecode, rate) ?? 0 : 0 };
}
function positionLabel(seconds: number, timing: NoteTiming | null): string {
  if (timing) return formatTimecode(framesToTimecode(timing.startFrames + ticksToFrames(secondsToTicks(seconds), timing.rate), timing.rate));
  return clock(seconds);
}

/** Approve or reject (request changes), with an optional comment that also goes into the thread. */
export function DecisionBox({ a }: { a: ApprovalDTO }) {
  const qc = useQueryClient();
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const changes = a.source !== 'flow';
  async function decide(decision: 'approved' | 'rejected') {
    setBusy(true);
    try {
      await api(`/api/v1/approvals/${a.id}/decision`, { method: 'POST', json: { decision, comment: comment || undefined } });
      toast(decision === 'approved' ? (a.source === 'flow' ? 'Approved; the run continues' : 'Approved') : changes ? 'Changes requested' : 'Rejected');
      setComment('');
      await Promise.all([qc.invalidateQueries({ queryKey: queryKeys.approvalsAll() }), qc.invalidateQueries({ queryKey: queryKeys.approvalNotes(a.id) }), qc.invalidateQueries({ queryKey: queryKeys.taskSummary() })]);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} data-testid="decision-box">
      <input className="input" style={{ flex: 1, minWidth: 200 }} placeholder="Comment (optional)" maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} aria-label="Decision comment" data-testid="decision-comment" />
      <button className="btn" disabled={busy} onClick={() => void decide('rejected')} data-testid="reject"><X size={14} /> {changes ? 'Request changes' : 'Reject'}</button>
      <button className="btn primary" disabled={busy} onClick={() => void decide('approved')} data-testid="approve"><Check size={14} /> Approve</button>
    </div>
  );
}

/** The request (its instructions) as the thread's first message, from whoever asked. */
export function requestMessage(a: ApprovalDTO): ThreadMessage | null {
  if (!a.instructions) return null;
  return {
    id: `request-${a.id}`, testId: 'approval-request', author: a.requestedBy ?? (a.source === 'flow' ? a.run?.definitionName ?? 'Workflow' : 'Someone'),
    mine: a.requestedByMe, createdAt: a.createdAt, body: a.instructions, verb: 'asked for approval',
  };
}

/** What the server writes as a decision's note when the reviewer left no comment. */
const DECISION_DEFAULT_BODIES = new Set(['Approved', 'Rejected', 'Requested changes']);

/**
 * The thread of one approval (0.99.1: a conversation). The request is the first message, from whoever asked; notes
 * written on the link page carry the reviewer's name; a decision is a bubble with its badge and comment, or a centred
 * event when there was no comment. A note can be at the playhead when the player is next to it.
 */
export function ApprovalThread({ approval: a, timing, assetId, currentSeconds, onSeek }: {
  approval: ApprovalDTO; timing: NoteTiming | null; assetId: string | null; currentSeconds?: number; onSeek?: (seconds: number) => void;
}) {
  const approvalId = a.id;
  const qc = useQueryClient();
  const notes = useApprovalNotes(approvalId);
  const [text, setText] = useState('');
  const [atPlayhead, setAtPlayhead] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    try {
      await api(`/api/v1/approvals/${approvalId}/notes`, { method: 'POST', json: { body: text, seconds: atPlayhead && currentSeconds != null ? currentSeconds : null } });
      setText('');
      await Promise.all([qc.invalidateQueries({ queryKey: queryKeys.approvalNotes(approvalId) }), qc.invalidateQueries({ queryKey: queryKeys.approvalsAll() })]);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const timecode = (seconds: number) => onSeek
    ? <button className="pill pill-button" onClick={() => onSeek(seconds)} title="Go to this point" data-testid="note-timecode"><Clock size={10} style={{ marginRight: 3 }} />{positionLabel(seconds, timing)}</button>
    : assetId ? <Link className="pill" to={`/assets/${assetId}?t=${seconds}`} data-testid="note-timecode"><Clock size={10} style={{ marginRight: 3 }} />{positionLabel(seconds, timing)}</Link>
    : <span className="pill">{positionLabel(seconds, timing)}</span>;
  const messages: ThreadMessage[] = [];
  const request = requestMessage(a);
  if (request) messages.push(request);
  for (const n of notes.data?.items ?? []) {
    const decision = n.kind === 'decision';
    const badge = decision ? (n.decision === 'approved'
      ? { tone: 'ready' as const, icon: <CircleCheck size={12} />, label: 'Approved' }
      : { tone: 'danger' as const, icon: <CircleX size={12} />, label: a.source === 'flow' ? 'Rejected' : 'Changes requested' }) : undefined;
    const comment = decision && DECISION_DEFAULT_BODIES.has(n.body.trim()) ? null : n.body;
    messages.push({
      id: n.id, testId: 'approval-note', author: n.author, external: n.external, mine: n.mine, createdAt: n.createdAt,
      body: comment, badge, event: decision && comment === null, meta: n.seconds != null ? timecode(n.seconds) : undefined,
    });
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="approval-thread">
      {/* Plain text only: nothing a reviewer writes is ever rendered as HTML. */}
      <Thread messages={messages} loaded={notes.data !== undefined} />
      <NoteComposer wide value={text} onChange={setText} onAdd={() => void add()} inputTestId="approval-note-input" addTestId="approval-note-add">
        {currentSeconds != null && (
          <label className="toggle-row" style={{ fontSize: 12 }}><input type="checkbox" checked={atPlayhead} onChange={(e) => setAtPlayhead(e.target.checked)} data-testid="note-at-playhead" /><span>At {positionLabel(currentSeconds, timing)}</span></label>
        )}
      </NoteComposer>
    </div>
  );
}

/** Tasks → Approvals. */
export function ApprovalsView() {
  const [params, setParams] = useSearchParams();
  const scope = (params.get('scope') ?? 'mine') as 'mine' | 'requested' | 'all';
  const status = (params.get('status') ?? 'pending') as 'pending' | 'approved' | 'rejected' | 'all';
  const q = useApprovals({ scope, status });
  const items = q.data?.items ?? [];
  const wanted = params.get('approval');
  const selectedId = wanted ?? items[0]?.id ?? null;
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v === null ? next.delete(k) : next.set(k, v));
    setParams(next, { replace: true });
  };
  return (
    <div className="tasks-layout">
      <div className="task-list" data-testid="approval-list">
        <div style={{ display: 'flex', gap: 8, padding: 10, flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
          <Segmented role="tablist" label="Whose approvals" value={scope} onChange={(v) => set({ scope: v === 'mine' ? null : v, approval: null })} options={[
            { value: 'mine', label: 'For me', testId: 'approvals-mine' },
            { value: 'requested', label: 'Asked by me', testId: 'approvals-requested' },
            { value: 'all', label: 'All', testId: 'approvals-all' },
          ]} />
          <Segmented role="tablist" label="Status" value={status} onChange={(s) => set({ status: s === 'pending' ? null : s, approval: null })}
            options={(['pending', 'approved', 'rejected', 'all'] as const).map((s) => ({ value: s, label: s === 'pending' ? 'Waiting' : s === 'approved' ? 'Approved' : s === 'rejected' ? 'Rejected' : 'All', testId: `approvals-status-${s}` }))} />
        </div>
        {q.isLoading ? <div className="skeleton" style={{ height: 120, margin: 12 }} /> : items.length === 0 ? (
          <EmptyState style={{ minHeight: 200 }} icon={<ListChecks size={22} />} title={scope === 'mine' && status === 'pending' ? 'Nothing waiting for you' : 'No approvals here'} />
        ) : items.map((a) => (
          <button key={a.id} className={`task-row ${a.id === selectedId ? 'active' : ''}`} onClick={() => set({ approval: a.id })} data-testid="approval-row">
            <ApprovalStatusIcon status={a.status} />
            <span className="task-row-main">
              <strong>{a.asset?.title ?? a.run?.definitionName ?? 'Approval'}</strong>
              <span className="faint">
                {SOURCE_ICON[a.source]} {a.source === 'flow' ? a.run?.definitionName : a.source === 'share' ? 'Share link' : `Asked by ${a.requestedBy ?? 'someone'}`} ·
                {a.approver.type === 'role' ? <Users size={11} /> : a.approver.type === 'user' ? <User size={11} /> : <Link2 size={11} />} {approverLabel(a)}
              </span>
            </span>
            {a.unseen && <span className="pill accent" data-testid="approval-unseen">New</span>}
            <span className="faint" style={{ fontSize: 12 }}>{relative(a.createdAt)}</span>
          </button>
        ))}
        <LoadMore list={q} shown={items.length} noun="approvals" />
      </div>
      {selectedId ? <ApprovalDetail key={selectedId} id={selectedId} /> : <div className="page muted">Choose an approval.</div>}
    </div>
  );
}

function ApprovalDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useApproval(id);
  const a = q.data;
  const asset = useAsset(a?.asset?.id ?? null);
  const timing = useMemo(() => timingOf(asset.data), [asset.data]);
  // Opening it marks what was new as seen (the server did on this read): refresh the badge and the list.
  const mine = Boolean(a?.requestedByMe);
  useEffect(() => {
    if (!mine) return;
    void qc.invalidateQueries({ queryKey: queryKeys.taskSummary() });
    void qc.invalidateQueries({ queryKey: queryKeys.approvalsAll(), predicate: (x) => x.queryKey[1] !== 'one' });
  }, [mine, id, qc]);
  if (q.isLoading) return <div className="page"><div className="skeleton" style={{ height: 160 }} /></div>;
  if (!a) return <div className="page muted">This approval is not available.</div>;
  return (
    <div className="page task-detail" data-testid="approval-detail">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <ApprovalStatusIcon status={a.status} />
        <span className="pill" data-testid="approval-status">{statusLabel(a)}</span>
        <span className="faint">{relative(a.createdAt)}</span>
      </div>
      <h2 style={{ margin: 0 }}>{a.asset?.title ?? a.run?.definitionName}</h2>
      <dl className="kv">
        {a.asset && <><dt>Asset</dt><dd><Film size={12} /> <Link to={`/assets/${a.asset.id}`} data-testid="approval-open-asset">{a.asset.title}</Link> <span className="faint mono">{a.asset.assetCode}</span></dd></>}
        <dt>Who decides</dt><dd>{approverLabel(a)}</dd>
        {a.source === 'flow' && a.run && <><dt>Workflow</dt><dd><Workflow size={12} /> <Link to={`/workflows/runs/${a.run.id}`}>{a.run.definitionName}</Link></dd></>}
        {a.source === 'share' && a.share && <><dt>Link</dt><dd><Link2 size={12} /> /s/{a.share.tokenHint}… · {a.share.state === 'active' ? (a.share.expiresAt ? `expires ${dateTime(a.share.expiresAt)}` : 'never expires') : a.share.state}</dd></>}
        {a.requestedBy && <><dt>Asked by</dt><dd>{a.requestedBy}</dd></>}
        {a.decidedBy && <><dt>Decided</dt><dd>{a.decidedBy}{a.decidedExternally ? ' (via link)' : ''} · {a.decidedAt ? relative(a.decidedAt) : ''}</dd></>}
      </dl>
      {a.canDecide && <DecisionBox a={a} />}
      {a.status === 'pending' && !a.canDecide && (
        <span className="muted">{a.source === 'share' ? 'Reviewers decide on the link page.' : `Waiting for ${a.approver.type === 'user' ? a.approver.name : `a ${a.approver.type === 'role' ? a.approver.name : 'reviewer'}`}.`}</span>
      )}
      <section className="panel-card">
        <p className="section-label">Notes</p>
        <ApprovalThread approval={a} timing={timing} assetId={a.asset?.id ?? null} />
      </section>
    </div>
  );
}
